import React, { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { getIncentives } from 'apis/CommunityAPI';
import { usePublicationContext } from 'components/Publication/PublicationContext';
import { useSalesBattlecardContext } from 'components/SalesBattlecard/SalesBattlecardContext';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import useDataQuarterSelection from 'hooks/useDataQuarterSelection';
import { useAppSelector } from 'store/hook';
import { getIncentiveQuarterDateRange } from 'utils/dateQuarter';

import Incentives from './Incentives';
import { getFilename } from '../GraphWidget/utils';
import NoDataInfo from '../Widget/NoDataContent';
import { useWidgetContext } from '../Widget/WidgetContext';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  justify-content: start;
  height: 100%;
`;

export const IncentivesWidgetContent = () => {
  const { contextCompany, selectedCommunity } = useAppSelector((state) => state.global);
  const { communityId } = useCommunityViewContext();
  const { selectedCompetitorId: battlecardCompetitorId } = useSalesBattlecardContext();
  const { publication } = usePublicationContext();
  const { filters = { competitors: [], careTypes: [] }, name, setCSVData, setFilename } = useWidgetContext();
  const { selection, isLatestQuarterSelected, quarters } = useDataQuarterSelection();
  const { competitors, careTypes } = filters;

  const effectiveCompetitors = battlecardCompetitorId !== null ? [battlecardCompetitorId] : competitors;
  const quarterSlugs = useMemo(() => quarters.map((quarter) => quarter.slug), [quarters]);
  const { startDate: incentiveStartDate, endDate: incentiveEndDate } = useMemo(
    () => getIncentiveQuarterDateRange(selection?.selectedQuarterSlug ?? null, quarterSlugs, isLatestQuarterSelected),
    [selection?.selectedQuarterSlug, quarterSlugs, isLatestQuarterSelected]
  );

  const fetchIncentivesParams = useMemo(
    () => ({
      status: 1,
      competitors: effectiveCompetitors?.length > 0 ? effectiveCompetitors : null,
      care_types: careTypes?.length > 0 ? careTypes : null,
      ...(incentiveStartDate ? { start_created_at: incentiveStartDate } : {}),
      ...(incentiveEndDate ? { end_created_at: incentiveEndDate } : {}),
      ...(publication?.hash ? { public_hash: publication.hash } : {}),
    }),
    [effectiveCompetitors, careTypes, incentiveStartDate, incentiveEndDate, publication?.hash]
  );

  const { data, isLoading } = useQuery({
    queryKey: ['community', 'incentive', communityId, battlecardCompetitorId, fetchIncentivesParams, contextCompany],
    queryFn: () => getIncentives(communityId, fetchIncentivesParams),
    refetchOnWindowFocus: false,
    enabled: Boolean(communityId),
  });

  const hasData = data?.length > 0;

  useEffect(() => {
    if (!hasData) {
      const exportData = [['COMMUNITY', 'INCENTIVE', 'CARE TYPE', 'ADDED']];
      setFilename(getFilename(selectedCommunity?.name || 'Community', name));
      setCSVData(exportData);
    }
  }, [data]);

  return (
    <Container data-testid="incentives-widget">
      {!isLoading && !hasData && <NoDataInfo data-testid="incentives-widget-empty">No Data Available</NoDataInfo>}
      {!isLoading && hasData && <Incentives data={data} />}
    </Container>
  );
};

export default IncentivesWidgetContent;
